import React from 'react'
import * as ROUTES from 'constants/routes'
import { Flex, Text, Link } from '@workwhile/ui'
import { ColumnDef } from '@tanstack/react-table'
import { VndlyPositionIssuesItem } from 'api/admin/vndly/listVndlyPositionIssues'
import { useVndlyPositionIssuesContextValue } from '../context/VndlyPositionIssuesProvider'
import { useVndlyIssuesContextValue } from 'pages/VndlyIssues/context/VndlyIssuesProvider'
import { LinkVndlyPositionButton } from './LinkVndlyPositionButton'
import { PaginatedTable } from 'components/Table/PaginatedTable'

const InstanceCell = ({ instanceId }: { instanceId: number }) => {
  const {
    state: { instances },
  } = useVndlyIssuesContextValue()

  const instance = instances.find((instance) => instance.id === instanceId)

  return <Text>{instance?.instanceUrl}</Text>
}

const CompanyCell = ({
  instanceId,
  companyId,
}: {
  instanceId: number
  companyId: number
}) => {
  const {
    state: { instances },
  } = useVndlyIssuesContextValue()

  const company = instances
    .find((instance) => instance.id === instanceId)
    ?.companies.find((company) => company.id === companyId)

  if (!company) {
    return <Text>(unknown)</Text>
  }

  return (
    <Text>
      {company.name} (
      <Link
        to={ROUTES.COMPANY_DETAIL.replace(':companyId', company.id.toString())}
        target="_blank"
      >
        {company.id}
      </Link>
      )
    </Text>
  )
}

const columns: ColumnDef<VndlyPositionIssuesItem>[] = [
  {
    header: 'Instance',
    accessorKey: 'instanceId',
    cell: ({ row }) => <InstanceCell instanceId={row.original.instanceId} />,
  },
  {
    header: 'Company',
    accessorKey: 'companyId',
    cell: ({ row }) => (
      <CompanyCell
        instanceId={row.original.instanceId}
        companyId={row.original.companyId}
      />
    ),
  },
  {
    header: 'Position',
    accessorKey: 'positionId',
    cell: ({ row }) => (
      <Text>
        {row.original.positionName} ({row.original.positionId})
      </Text>
    ),
  },
  {
    header: 'Issues',
    cell: ({ row }) => {
      return (
        <Text style={{ whiteSpace: 'nowrap' }}>
          {row.original.issues.join(', ')}
        </Text>
      )
    },
  },
  {
    header: 'Actions',
    cell: ({ row }) => {
      return (
        <Flex gap={2} flexWrap="wrap">
          <LinkVndlyPositionButton
            companyPositionId={row.original.positionId}
            positionName={row.original.positionName}
            companyId={row.original.companyId}
            vndlyInstanceId={row.original.instanceId}
          />
        </Flex>
      )
    },
  },
]

export const VndlyPositionIssuesMain = () => {
  const {
    state: { data, pagination, isFetching },
    actions: { setPagination },
  } = useVndlyPositionIssuesContextValue()

  return (
    <PaginatedTable
      columns={columns}
      items={data.items}
      rowCount={data.totalRows}
      pagination={pagination}
      setPagination={setPagination}
      isFetching={isFetching}
      emptyMessage="No position issues found."
      emptyStateHeight={50}
    />
  )
}
